import { useTranslate } from "ra-core";
import { useState } from "react";

import { LIFE_WHEEL_MAX_SCORE, type ChartTopic } from "./lifeWheelTopics";

const VIEW_SIZE = 480;
const CENTER = VIEW_SIZE / 2;
const RADIUS = 150;
const LABEL_RADIUS = RADIUS + 22;
const RINGS = [2, 4, 6, 8, 10];
const CURRENT_COLOR = "var(--chart-1)";
const TARGET_COLOR = "var(--chart-2)";

const angleAt = (index: number, count: number) =>
  -Math.PI / 2 + (2 * Math.PI * index) / count;

const pointAt = (index: number, count: number, distance: number) => {
  const angle = angleAt(index, count);
  return {
    x: CENTER + distance * Math.cos(angle),
    y: CENTER + distance * Math.sin(angle),
  };
};

const toPoints = (values: number[]) =>
  values
    .map((value, index) => {
      const { x, y } = pointAt(
        index,
        values.length,
        (value / LIFE_WHEEL_MAX_SCORE) * RADIUS,
      );
      return `${x},${y}`;
    })
    .join(" ");

const labelAnchor = (index: number, count: number) => {
  const cos = Math.cos(angleAt(index, count));
  if (cos > 0.1) return "start";
  if (cos < -0.1) return "end";
  return "middle";
};

export interface LifeWheelRadarProps {
  topics: ChartTopic[];
}

export const LifeWheelRadar = ({ topics }: LifeWheelRadarProps) => {
  const translate = useTranslate();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const count = topics.length;
  const hovered = hoveredIndex != null ? topics[hoveredIndex] : null;
  const topicName = (topic: ChartTopic["topic"]) =>
    translate(`crm.personal.life_wheel.topics.${topic}.name`);

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative w-full max-w-[480px]">
        <svg
          viewBox={`0 0 ${VIEW_SIZE} ${VIEW_SIZE}`}
          className="h-auto w-full overflow-visible"
          role="img"
          aria-label={translate("crm.personal.life_wheel.title")}
          data-testid="life-wheel-radar"
          data-axes={count}
        >
          {RINGS.map((ring) => (
            <polygon
              key={ring}
              points={toPoints(Array(count).fill(ring))}
              fill="none"
              stroke="var(--color-border)"
              strokeWidth={1}
            />
          ))}
          {topics.map((_, index) => {
            const { x, y } = pointAt(index, count, RADIUS);
            return (
              <line
                key={index}
                x1={CENTER}
                y1={CENTER}
                x2={x}
                y2={y}
                stroke="var(--color-border)"
                strokeWidth={1}
              />
            );
          })}
          {RINGS.map((ring) => (
            <text
              key={ring}
              x={CENTER + 4}
              y={CENTER - (ring / LIFE_WHEEL_MAX_SCORE) * RADIUS}
              fontSize={10}
              fill="var(--color-muted-foreground)"
            >
              {ring}
            </text>
          ))}

          <polygon
            points={toPoints(topics.map(({ target }) => target))}
            fill={TARGET_COLOR}
            fillOpacity={0.08}
            stroke={TARGET_COLOR}
            strokeWidth={2}
            strokeDasharray="6 4"
          />
          <polygon
            points={toPoints(topics.map(({ current }) => current))}
            fill={CURRENT_COLOR}
            fillOpacity={0.25}
            stroke={CURRENT_COLOR}
            strokeWidth={2}
          />

          {topics.map(({ topic, current, target }, index) => {
            const currentPoint = pointAt(
              index,
              count,
              (current / LIFE_WHEEL_MAX_SCORE) * RADIUS,
            );
            const targetPoint = pointAt(
              index,
              count,
              (target / LIFE_WHEEL_MAX_SCORE) * RADIUS,
            );
            const labelPoint = pointAt(index, count, LABEL_RADIUS);
            const isHovered = hoveredIndex === index;
            return (
              <g
                key={topic}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                className="cursor-default"
              >
                <circle
                  cx={targetPoint.x}
                  cy={targetPoint.y}
                  r={isHovered ? 5 : 3}
                  fill={TARGET_COLOR}
                />
                <circle
                  cx={currentPoint.x}
                  cy={currentPoint.y}
                  r={isHovered ? 5 : 3}
                  fill={CURRENT_COLOR}
                />
                <text
                  x={labelPoint.x}
                  y={labelPoint.y}
                  textAnchor={labelAnchor(index, count)}
                  dominantBaseline="middle"
                  fontSize={13}
                  fontWeight={isHovered ? 600 : 400}
                  fill="var(--color-foreground)"
                >
                  {topicName(topic)}
                </text>
                {/* Larger invisible hit area for hovering */}
                <circle
                  cx={labelPoint.x}
                  cy={labelPoint.y}
                  r={28}
                  fill="transparent"
                />
              </g>
            );
          })}
        </svg>

        {hovered && hoveredIndex != null && (
          <div
            className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-md bg-secondary px-3 py-2 text-xs text-secondary-foreground shadow"
            style={{
              left: `${(pointAt(hoveredIndex, count, LABEL_RADIUS).x / VIEW_SIZE) * 100}%`,
              top: `${((pointAt(hoveredIndex, count, LABEL_RADIUS).y - 14) / VIEW_SIZE) * 100}%`,
            }}
          >
            <p className="font-semibold">{topicName(hovered.topic)}</p>
            <p>
              {translate("crm.personal.life_wheel.current")}: {hovered.current}
            </p>
            <p>
              {translate("crm.personal.life_wheel.target")}: {hovered.target}
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span
            className="size-3 rounded-sm"
            style={{ backgroundColor: CURRENT_COLOR }}
          />
          {translate("crm.personal.life_wheel.current")}
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="h-0 w-4 border-t-2 border-dashed"
            style={{ borderColor: TARGET_COLOR }}
          />
          {translate("crm.personal.life_wheel.target")}
        </span>
      </div>
    </div>
  );
};
